// Pure display helpers for the read-only weekly zone schedule. Kept out of the
// React component so the day ordering, time formatting and per-event target
// interpretation can be unit-tested in isolation.
//
// The reported schedule (darwin_core zone.schedule) is a nested, per-day shape:
//   [{ day: 'Monday', events: [{ startTime, setpoint | on }, ...] }, ...]
// where startTime is minutes since local midnight and each event carries EITHER
//   - `on` (boolean)  -> a hot-water (DHW) on/off period, no temperature; or
//   - `setpoint`      -> heating: a number in centidegrees (SI), or the string
//                        'comfort' / 'reduced' (HPI heat pump), whose actual
//                        temperature lives on the zone (getHpSetpoint) and is
//                        passed in here as comfort/reducedSetpoint centidegrees.

import {
  weekifySchedule,
  getCurrentEvent as scheduleGetCurrentEvent,
  getNextEvent as scheduleGetNextEvent,
} from 'darwin_core/schedule/schedule.js';

import { formatTemperature } from './formatZone.js';

// Display order for the week. Monday-first, matching darwin_core's DAYS_OF_WEEK.
export const WEEK_DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

// A start time is minutes since local midnight; render it as a zero-padded 24h
// clock ("06:30"). Minutes are clamped into a single day so a value that has
// rolled past midnight still shows a real time, and non-numeric/NaN input yields
// a neutral placeholder rather than "NaN:NaN" from a malformed frame.
export function formatEventTime(minutesSinceMidnight) {
  if (
    typeof minutesSinceMidnight !== 'number' ||
    !Number.isFinite(minutesSinceMidnight)
  ) {
    return '--:--';
  }
  const mins = ((Math.round(minutesSinceMidnight) % 1440) + 1440) % 1440;
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

// Same instant as formatEventTime, but as a 12-hour clock with an am/pm suffix
// ("06:30" -> "6:30am", "22:00" -> "10:00pm") for the property tile's "Until …"
// line, which reads as prose rather than a schedule-grid time.
export function formatClock12(minutesSinceMidnight) {
  if (
    typeof minutesSinceMidnight !== 'number' ||
    !Number.isFinite(minutesSinceMidnight)
  ) {
    return '--:--';
  }
  const mins = ((Math.round(minutesSinceMidnight) % 1440) + 1440) % 1440;
  const period = mins < 720 ? 'am' : 'pm';
  const hours24 = Math.floor(mins / 60);
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const minutes = mins % 60;
  return `${hours12}:${String(minutes).padStart(2, '0')}${period}`;
}

// Describes what a single event sets, faithful to the reported shape. Returns a
// { kind, label } pair; kind is only used for styling/testing hooks, label is the
// human-readable target. HPI comfort/reduced events resolve to the zone's
// comfort/reduced setpoint (centidegrees) supplied by the caller.
export function describeEventTarget(
  event,
  { comfortSetpoint, reducedSetpoint } = {},
) {
  if (!event || typeof event !== 'object') {
    return { kind: 'unknown', label: '—' };
  }

  // DHW: an on/off period. Reported as a boolean `on`, never a temperature.
  if (event.on !== undefined) {
    return { kind: 'dhw', label: event.on ? 'On' : 'Off' };
  }

  // HPI heating: the setpoint is a named level; the temperature comes from the
  // zone-level comfort/reduced setpoint (via the getHpSetpoint selector).
  if (event.setpoint === 'comfort' || event.setpoint === 'reduced') {
    const isComfort = event.setpoint === 'comfort';
    const name = isComfort ? 'Comfort' : 'Setback';
    const temp = formatTemperature(
      isComfort ? comfortSetpoint : reducedSetpoint,
    );
    return { kind: 'hpi', label: temp ? `${name} · ${temp}` : name };
  }

  // SI heating: a numeric setpoint in centidegrees. The wire type isn't pinned —
  // some frames deliver the setpoint as a numeric string — so coerce a
  // numeric-looking string before formatting (empty/whitespace and non-numeric
  // strings fall through to the dash rather than becoming 0.0°C).
  const raw = event.setpoint;
  const centidegrees =
    typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : raw;
  const temp = formatTemperature(centidegrees);
  if (temp != null) {
    return { kind: 'heating', label: temp };
  }

  // Unknown/missing target — guard so a stale frame renders a dash, not a crash.
  return { kind: 'unknown', label: '—' };
}

// Turns the reported nested schedule into an ordered, per-day view model:
//   [{ day, events: [{ startTime, timeLabel, kind, label }, ...] }, ...]
// Days are emitted in WEEK_DAYS order; days absent from the schedule are skipped
// (a day present but with no events is kept, so the UI can say "No events").
// A missing/empty/non-array schedule yields [] so callers can show an empty state.
export function buildWeeklySchedule(rawSchedule, hpSetpoints = {}) {
  if (!Array.isArray(rawSchedule) || rawSchedule.length === 0) {
    return [];
  }

  const byDay = new Map();
  for (const entry of rawSchedule) {
    if (entry && typeof entry.day === 'string' && Array.isArray(entry.events)) {
      byDay.set(entry.day, entry.events);
    }
  }

  const result = [];
  for (const day of WEEK_DAYS) {
    const events = byDay.get(day);
    if (!events) {
      continue;
    }
    const sorted = [...events].sort(
      (a, b) => (a?.startTime ?? 0) - (b?.startTime ?? 0),
    );
    result.push({
      day,
      events: sorted.map((event) => ({
        startTime: event?.startTime,
        timeLabel: formatEventTime(event?.startTime),
        ...describeEventTarget(event, hpSetpoints),
      })),
    });
  }
  return result;
}

// Resolves which event is on now and which is next, keyed back to the rendered
// rows by { day, minutes-since-local-midnight }.
//
// This is derived from the REPORTED schedule only, via darwin_core's low-level
// current/next helpers — deliberately NOT the state-level getCurrentEvent/
// getNextEvent selectors. Those fold in a live boost/DSR override end time and
// return a SYNTHETIC event timed to the override's end, which never matches a
// real scheduled row: the effect is that the "Next" badge silently vanishes
// whenever a boost is running (a view-only web user can trigger one from mobile).
// A pure weekly view wants the schedule's own next event, so we skip the override
// entirely and read the same data the rows are built from.
//
// weekifySchedule does `entry.events.map(p => ({ startTime: ... + p.startTime }))`
// with no guard, so a partial/malformed frame throws here and, with no error
// boundary in scope, takes down the whole render. buildWeeklySchedule tolerates
// the same shapes (skips entries without an events array; renders a malformed
// event as a dash), so mirror that here at BOTH levels — drop entries missing a
// day/events array, and drop non-object elements within a day — so the two paths
// defend identical input. A missing/empty/all-malformed schedule yields {}.
export function getScheduleNowNext(rawSchedule) {
  if (!Array.isArray(rawSchedule) || rawSchedule.length === 0) {
    return {};
  }

  const sanitised = rawSchedule
    .filter(
      (entry) =>
        entry && typeof entry.day === 'string' && Array.isArray(entry.events),
    )
    .map((entry) => ({
      ...entry,
      events: entry.events.filter((e) => e && typeof e === 'object'),
    }));
  if (sanitised.length === 0) {
    return {};
  }

  const weekified = weekifySchedule(sanitised);
  const current = scheduleGetCurrentEvent(weekified);
  const next = scheduleGetNextEvent(weekified);

  return {
    currentDay: current?.day,
    currentStart: current?.minutesSinceMidnight,
    nextDay: next?.day,
    nextStart: next?.minutesSinceMidnight,
  };
}
